import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  AsyncSection,
  DataTable,
  Form,
  FormError,
  Panel,
  SelectField,
  SubmitButton,
  TextField,
  useNotifications,
  type Column,
} from '../../components';
import {
  Artefact,
  StageStatus,
  WalkMode,
  type WalkStage,
  type EsocialPortalWalkActionScreenshot,
  type EsocialPortalWalkCertificateHop,
  type EsocialPortalWalkResponse,
  type EsocialPortalWalkStage,
} from '../../api';
import { formatDateTime, formatInteger } from '../../format';
import { staffEsocialPortalWalkPath } from '../../routes/paths';
import { useRequiredSession } from '../../session';
import { saveAsAttachment } from '../../utils/download';
import {
  useCompanies,
  useEsocialPortalBrowser,
  useEsocialPortalWalk,
  useEsocialPortalWalkActionScreenshot,
  useEsocialPortalWalkArtefact,
  useEsocialPortalWalkBundle,
  useEsocialPortalWalkScreenshot,
  useEsocialPortalWalkTrace,
  useEsocialPortalWalks,
  useRunEsocialPortalWalk,
} from '../queries';
import { EsocialPortalSessionPanel } from './EsocialPortalSessionPanel';
import { ENVIRONMENT_LABELS } from './esocial';
import {
  BROWSER_BACKEND_LABELS,
  PORTAL_SESSION_RUN_PERMISSION,
  PORTAL_WALK_COPY,
  PORTAL_WALK_READ_PERMISSION,
  PORTAL_WALK_RUN_PERMISSION,
  REPORT_STATE_LABELS,
  SKIPPED_REASONS,
  STAGE_FAILURE_MESSAGES,
  STAGE_STATUS_LABELS,
  STAGE_STATUS_TONES,
  WALK_ACTION_LABELS,
  WALK_MODE_LABELS,
  WALK_MODE_SHORT,
  WALK_OUTCOME_LABELS,
  WALK_OUTCOME_TONES,
  WALK_STAGES_OF,
  WALK_STAGE_LABELS,
  parseTimeout,
  walkStartFailureMessage,
  type PortalWalkTraceRequest,
} from './portalWalk';

/**
 * The eSocial web portal, walked stage by stage from a screen (#452).
 *
 * #451 built the walk and seals what every stage saw, but read back through
 * gated endpoints it is curl, a token and a dozen requests per run. The person
 * who runs walks on staging is the product manager, who holds the certificate
 * there and nowhere else, and who has to bring a run back to a conversation.
 * This tab is that: start a walk, read its stages and screens, download the
 * whole run as one file.
 *
 * Four decisions shape it.
 *
 * **The run endpoint answers when the walk is over**, so the walk in progress
 * is the mutation's pending state and nothing else. The button says so, a
 * sentence under it says a walk takes minutes, and there is no second button to
 * press meanwhile. No run is visible to anybody until it has finished, so there
 * is nothing to poll.
 *
 * **The company and the run live in the address** -- `?empresa=` and
 * `?percurso=`, opaque uuids -- so a link to one run can be pasted into a
 * conversation and lands on it. What a walk was asked about never does.
 *
 * **A walk that failed at a stage is a result.** It is rendered as its stages,
 * the failed one red and the ones after it `Não executada` in a neutral tone,
 * with the screenshot of where it got stuck -- which is the reason for running
 * it at all. Only a walk that could not *start* is a form error.
 *
 * **A stage is diagnosable from the screen alone** (#456): under its full-page
 * screenshot, the viewport after each action as thumbnails that open full
 * size, and its trace -- the page's requests with the ones still pending when
 * it ended marked, and its console. The run says the timeout it ran with, and
 * the form lets the next run ask for a longer one.
 *
 * **Each part asks for its own permission.** The form is mounted only for a
 * principal that holds the one #451 gates the run with, and the history and
 * the evidence only for one that holds the read; a part that would be refused
 * is not offered, and its request is never made.
 */
export function EsocialPortalWalkPanel() {
  const session = useRequiredSession();
  const permissions = session.principal.permissions;
  const mayRun = permissions.includes(PORTAL_WALK_RUN_PERMISSION);
  const mayRead = permissions.includes(PORTAL_WALK_READ_PERMISSION);
  const mayRunSession = permissions.includes(PORTAL_SESSION_RUN_PERMISSION);
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = searchParams.get('empresa');
  const runId = searchParams.get('percurso');

  const chooseCompany = (next: string) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (next === '') {
        params.delete('empresa');
      } else {
        params.set('empresa', next);
      }
      // A run belongs to one company; a different company is a different page.
      params.delete('percurso');
      return params;
    });
  };

  const openRun = (run: EsocialPortalWalkResponse) => {
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      params.set('empresa', run.company_id);
      params.set('percurso', run.id);
      return params;
    });
  };

  if (!mayRun && !mayRead) {
    return (
      <p className="panel__footnote" role="status">
        {PORTAL_WALK_COPY.noRunPermission}
      </p>
    );
  }

  return (
    <>
      {mayRun ? (
        <StartWalk companyId={companyId} onCompany={chooseCompany} onStarted={openRun} />
      ) : (
        <CompanyFilter companyId={companyId} onCompany={chooseCompany} />
      )}
      {mayRunSession && <EsocialPortalSessionPanel />}
      {mayRead && runId !== null && <WalkRun runId={runId} />}
      {mayRead && <WalkHistory companyId={companyId} />}
    </>
  );
}

function companyOptions(companies: ReturnType<typeof useCompanies>) {
  return (companies.data?.items ?? []).map((company) => ({
    value: company.id,
    label: company.name,
  }));
}

/** The company select alone, for a reader who may not start walks. */
function CompanyFilter({
  companyId,
  onCompany,
}: {
  companyId: string | null;
  onCompany: (companyId: string) => void;
}) {
  const companies = useCompanies();
  return (
    <Panel title={PORTAL_WALK_COPY.historyTitle}>
      <SelectField
        label={PORTAL_WALK_COPY.company}
        value={companyId ?? ''}
        onChange={onCompany}
        options={companyOptions(companies)}
        placeholder={
          companies.isPending
            ? PORTAL_WALK_COPY.companiesLoading
            : PORTAL_WALK_COPY.companyPlaceholder
        }
      />
    </Panel>
  );
}

function StartWalk({
  companyId,
  onCompany,
  onStarted,
}: {
  companyId: string | null;
  onCompany: (companyId: string) => void;
  onStarted: (run: EsocialPortalWalkResponse) => void;
}) {
  const companies = useCompanies();
  const walk = useRunEsocialPortalWalk();
  // Asked once: whether a person at a remote browser has to sign in (#477).
  const remote = useEsocialPortalBrowser().data?.remote === true;
  const [mode, setMode] = useState<WalkMode>(WalkMode.REQUEST);
  const [stopAfter, setStopAfter] = useState<WalkStage | ''>('');
  const [timeoutText, setTimeoutText] = useState('');
  const [companyMissing, setCompanyMissing] = useState(false);
  const [timeoutInvalid, setTimeoutInvalid] = useState(false);

  const stages = WALK_STAGES_OF[mode];
  const specific = walkStartFailureMessage(walk.error);

  const chooseMode = (next: WalkMode | '') => {
    if (next === '') {
      return;
    }
    setMode(next);
    // A stage the new mode does not walk would be refused before launch.
    if (stopAfter !== '' && !WALK_STAGES_OF[next].includes(stopAfter)) {
      setStopAfter('');
    }
  };

  const submit = () => {
    const seconds = parseTimeout(timeoutText);
    setTimeoutInvalid(seconds === undefined);
    if (companyId === null) {
      setCompanyMissing(true);
      return;
    }
    setCompanyMissing(false);
    if (seconds === undefined) {
      return;
    }
    walk.mutate(
      {
        company_id: companyId,
        mode,
        ...(stopAfter !== '' && { stop_after: stopAfter }),
        ...(seconds !== null && { timeout_seconds: seconds }),
      },
      { onSuccess: onStarted },
    );
  };

  return (
    <Panel title={PORTAL_WALK_COPY.startTitle} description={PORTAL_WALK_COPY.startDescription}>
      <Form onSubmit={submit} aria-label={PORTAL_WALK_COPY.startTitle}>
        <FormError error={walk.error} {...(specific !== undefined && { message: specific })} />
        <SelectField
          label={PORTAL_WALK_COPY.company}
          value={companyId ?? ''}
          onChange={(next) => {
            setCompanyMissing(false);
            onCompany(next);
          }}
          options={companyOptions(companies)}
          placeholder={
            companies.isPending
              ? PORTAL_WALK_COPY.companiesLoading
              : PORTAL_WALK_COPY.companyPlaceholder
          }
          hint={PORTAL_WALK_COPY.companyHint}
          error={companyMissing ? PORTAL_WALK_COPY.companyPlaceholder : undefined}
          required
          disabled={walk.isPending}
        />
        <SelectField
          label={PORTAL_WALK_COPY.mode}
          value={mode}
          onChange={chooseMode}
          options={Object.values(WalkMode).map((value) => ({
            value,
            label: WALK_MODE_LABELS[value],
          }))}
          hint={PORTAL_WALK_COPY.modeHint}
          required
          disabled={walk.isPending}
        />
        <SelectField
          label={PORTAL_WALK_COPY.stopAfter}
          value={stopAfter}
          onChange={setStopAfter}
          options={stages.map((stage) => ({ value: stage, label: WALK_STAGE_LABELS[stage] }))}
          placeholder={PORTAL_WALK_COPY.stopAfterAll}
          hint={PORTAL_WALK_COPY.stopAfterHint}
          disabled={walk.isPending}
        />
        <TextField
          label={PORTAL_WALK_COPY.timeout}
          value={timeoutText}
          onChange={(next) => {
            setTimeoutInvalid(false);
            setTimeoutText(next);
          }}
          inputMode="numeric"
          hint={PORTAL_WALK_COPY.timeoutHint}
          error={timeoutInvalid ? PORTAL_WALK_COPY.timeoutInvalid : undefined}
          disabled={walk.isPending}
        />
        <SubmitButton
          pending={walk.isPending}
          pendingLabel={remote ? PORTAL_WALK_COPY.startingRemote : PORTAL_WALK_COPY.starting}
        >
          {PORTAL_WALK_COPY.start}
        </SubmitButton>
        {walk.isPending && (
          <p className="panel__footnote" role="status">
            {remote ? PORTAL_WALK_COPY.inProgressRemote : PORTAL_WALK_COPY.inProgress}
          </p>
        )}
      </Form>
    </Panel>
  );
}

function stagesCompleted(run: EsocialPortalWalkResponse): number {
  return run.stages.filter((stage) => stage.status === StageStatus.COMPLETED).length;
}

function WalkHistory({ companyId }: { companyId: string | null }) {
  const walks = useEsocialPortalWalks(companyId);

  const columns: readonly Column<EsocialPortalWalkResponse>[] = [
    {
      key: 'when',
      header: PORTAL_WALK_COPY.when,
      render: (run) => formatDateTime(run.started_at),
    },
    { key: 'who', header: PORTAL_WALK_COPY.who, render: (run) => run.requested_by_display_name },
    {
      key: 'mode',
      header: PORTAL_WALK_COPY.modeColumn,
      render: (run) => WALK_MODE_SHORT[run.mode],
    },
    {
      key: 'stopAfter',
      header: PORTAL_WALK_COPY.stopAfterColumn,
      render: (run) =>
        run.stop_after ? WALK_STAGE_LABELS[run.stop_after] : PORTAL_WALK_COPY.stopAfterNone,
    },
    {
      key: 'outcome',
      header: PORTAL_WALK_COPY.outcome,
      render: (run) => (
        <span className={`badge badge--${WALK_OUTCOME_TONES[run.outcome]}`}>
          {WALK_OUTCOME_LABELS[run.outcome]}
        </span>
      ),
    },
    {
      key: 'stages',
      header: PORTAL_WALK_COPY.stagesCompleted,
      render: (run) =>
        `${formatInteger(stagesCompleted(run))} de ${formatInteger(run.stage_count)}`,
    },
  ];

  return (
    <Panel title={PORTAL_WALK_COPY.historyTitle} description={PORTAL_WALK_COPY.historyDescription}>
      <AsyncSection
        state={walks}
        loadingLabel={PORTAL_WALK_COPY.historyTitle}
        errorTitle={PORTAL_WALK_COPY.historyFailed}
        isEmpty={(data) => data.items.length === 0}
        emptyTitle={PORTAL_WALK_COPY.historyEmpty}
        emptyDescription={PORTAL_WALK_COPY.historyEmptyDescription}
      >
        {(data) => (
          <DataTable
            id="esocial-portal-walks"
            caption={PORTAL_WALK_COPY.historyCaption}
            columns={columns}
            rows={data.items}
            rowKey={(run) => run.id}
            rowLink={(run) => staffEsocialPortalWalkPath(run.company_id, run.id)}
          />
        )}
      </AsyncSection>
    </Panel>
  );
}

function WalkRun({ runId }: { runId: string }) {
  const run = useEsocialPortalWalk(runId);
  return (
    <AsyncSection
      state={run}
      loadingLabel={PORTAL_WALK_COPY.runTitle}
      errorTitle={PORTAL_WALK_COPY.runFailed}
    >
      {(data) => <WalkRunResult run={data} />}
    </AsyncSection>
  );
}

function WalkRunResult({ run }: { run: EsocialPortalWalkResponse }) {
  const bundle = useEsocialPortalWalkBundle();
  const { notify } = useNotifications();
  const failed = run.stages.find((stage) => stage.status === StageStatus.FAILED);

  const columns: readonly Column<EsocialPortalWalkStage>[] = [
    {
      key: 'stage',
      header: PORTAL_WALK_COPY.stage,
      render: (stage) => WALK_STAGE_LABELS[stage.name],
    },
    {
      key: 'status',
      header: PORTAL_WALK_COPY.status,
      render: (stage) => (
        <span className={`badge badge--${STAGE_STATUS_TONES[stage.status]}`}>
          {STAGE_STATUS_LABELS[stage.status]}
        </span>
      ),
    },
    {
      key: 'moments',
      header: PORTAL_WALK_COPY.moments,
      render: (stage) =>
        stage.started_at ? (
          <>
            <p>{formatDateTime(stage.started_at)}</p>
            {stage.finished_at && (
              <p className="table__muted">{formatDateTime(stage.finished_at)}</p>
            )}
          </>
        ) : (
          '—'
        ),
    },
    {
      key: 'detail',
      header: PORTAL_WALK_COPY.detail,
      render: (stage) => <StageDetail stage={stage} />,
    },
  ];

  return (
    <Panel
      title={`${PORTAL_WALK_COPY.runTitle} de ${formatDateTime(run.started_at)}`}
      actions={
        <button
          type="button"
          className="button button--secondary"
          disabled={bundle.isPending}
          onClick={() =>
            bundle.mutate(
              { runId: run.id },
              {
                onSuccess: ({ blob, filename }) =>
                  saveAsAttachment(blob, filename ?? `percurso-portal-esocial-${run.id}.zip`),
                onError: () => notify('error', PORTAL_WALK_COPY.bundleFailed),
              },
            )
          }
        >
          {bundle.isPending ? PORTAL_WALK_COPY.bundlePending : PORTAL_WALK_COPY.bundle}
        </button>
      }
    >
      <p
        className={`panel__footnote${failed === undefined ? '' : ' panel__footnote--warning'}`}
        role="status"
      >
        {failed === undefined
          ? PORTAL_WALK_COPY.runCompleted
          : PORTAL_WALK_COPY.runFailedAt(WALK_STAGE_LABELS[failed.name])}
      </p>
      <dl className="definitions">
        <dt>{PORTAL_WALK_COPY.employer}</dt>
        <dd>{run.employer_cnpj_formatted}</dd>
        <dt>{PORTAL_WALK_COPY.mode}</dt>
        <dd>{WALK_MODE_LABELS[run.mode]}</dd>
        <dt>{PORTAL_WALK_COPY.stopAfter}</dt>
        <dd>
          {run.stop_after ? WALK_STAGE_LABELS[run.stop_after] : PORTAL_WALK_COPY.stopAfterNone}
        </dd>
        <dt>{PORTAL_WALK_COPY.environment}</dt>
        <dd>{ENVIRONMENT_LABELS[run.environment]}</dd>
        <dt>{PORTAL_WALK_COPY.timeoutRow}</dt>
        <dd>
          {run.timeout_seconds === null || run.timeout_seconds === undefined
            ? PORTAL_WALK_COPY.timeoutUnknown
            : PORTAL_WALK_COPY.timeoutValue(run.timeout_seconds)}
        </dd>
        <dt>{PORTAL_WALK_COPY.browserRow}</dt>
        <dd>
          {run.browser_backend
            ? BROWSER_BACKEND_LABELS[run.browser_backend]
            : PORTAL_WALK_COPY.browserUnknown}
        </dd>
        <dt>{PORTAL_WALK_COPY.requestedBy}</dt>
        <dd>{run.requested_by_display_name}</dd>
        <dt>{PORTAL_WALK_COPY.startedAt}</dt>
        <dd>{formatDateTime(run.started_at)}</dd>
        {run.finished_at && (
          <>
            <dt>{PORTAL_WALK_COPY.finishedAt}</dt>
            <dd>{formatDateTime(run.finished_at)}</dd>
          </>
        )}
      </dl>
      <DataTable
        caption={PORTAL_WALK_COPY.runCaption}
        columnVisibility={false}
        columns={columns}
        rows={run.stages}
        rowKey={(stage) => String(stage.index)}
        expansion={(stage) =>
          stage.artefacts.length > 0 ? <StageEvidence runId={run.id} stage={stage} /> : null
        }
      />
    </Panel>
  );
}

/**
 * What one stage found, in the portal's own words where it reported any.
 *
 * Our sentence first only where there is one to say -- why a stage did not
 * run. A failure is the server's sanitised English, shown as it came, under
 * the status that already says it failed; the screenshot below is the rest.
 */
function StageDetail({ stage }: { stage: EsocialPortalWalkStage }) {
  if (stage.status === StageStatus.SKIPPED) {
    return <p>{stage.skipped_reason ? SKIPPED_REASONS[stage.skipped_reason] : '—'}</p>;
  }
  if (stage.status === StageStatus.INHERITED) {
    return <p>{PORTAL_WALK_COPY.inheritedLogin}</p>;
  }
  return (
    <dl className="definitions">
      {stage.captcha_solved_by_person === true && (
        <>
          <dt>{PORTAL_WALK_COPY.captchaSolved}</dt>
          <dd>{PORTAL_WALK_COPY.captchaSolvedValue(stage.captcha_solve_seconds)}</dd>
        </>
      )}
      {stage.two_step_completed_by_person === true && (
        <>
          <dt>{PORTAL_WALK_COPY.twoStepCompleted}</dt>
          <dd>{PORTAL_WALK_COPY.twoStepCompletedValue(stage.two_step_seconds)}</dd>
        </>
      )}
      {stage.portal_message && (
        <>
          <dt>{PORTAL_WALK_COPY.portalMessage}</dt>
          <dd>{stage.portal_message}</dd>
        </>
      )}
      {stage.portal_state && (
        <>
          <dt>{PORTAL_WALK_COPY.portalState}</dt>
          <dd>
            {stage.portal_state}
            {stage.portal_state_parsed && (
              <span className="table__muted">
                {' '}
                ({REPORT_STATE_LABELS[stage.portal_state_parsed]})
              </span>
            )}
          </dd>
        </>
      )}
      {stage.profiles_offered !== undefined && stage.profiles_offered.length > 0 && (
        <>
          <dt>{PORTAL_WALK_COPY.profilesOffered}</dt>
          <dd>{stage.profiles_offered.join(', ')}</dd>
        </>
      )}
      {stage.report_type && (
        <>
          <dt>{PORTAL_WALK_COPY.reportType}</dt>
          <dd>{stage.report_type}</dd>
        </>
      )}
      {stage.employer_number && (
        <>
          <dt>{PORTAL_WALK_COPY.employerNumber}</dt>
          <dd>{stage.employer_number}</dd>
        </>
      )}
      {stage.records_retrieved !== null && stage.records_retrieved !== undefined && (
        <>
          <dt>{PORTAL_WALK_COPY.recordsRetrieved}</dt>
          <dd>{formatInteger(stage.records_retrieved)}</dd>
        </>
      )}
      {stage.file_name && (
        <>
          <dt>{PORTAL_WALK_COPY.downloadedFile}</dt>
          <dd>{stage.file_name}</dd>
        </>
      )}
      {stage.title && (
        <>
          <dt>{PORTAL_WALK_COPY.pageTitle}</dt>
          <dd>{stage.title}</dd>
        </>
      )}
      {stage.url && (
        <>
          <dt>{PORTAL_WALK_COPY.url}</dt>
          <dd>
            <code>{stage.url}</code>
          </dd>
        </>
      )}
      {stage.failure && (
        <>
          <dt>{PORTAL_WALK_COPY.failure}</dt>
          <dd className="table__muted">{stage.failure}</dd>
        </>
      )}
      {stage.failure_code && (
        <>
          <dt>{PORTAL_WALK_COPY.remedy}</dt>
          <dd>{STAGE_FAILURE_MESSAGES[stage.failure_code]}</dd>
        </>
      )}
    </dl>
  );
}

/** The stage's screenshot inline, and its HTML and downloaded file as attachments. */
function StageEvidence({ runId, stage }: { runId: string; stage: EsocialPortalWalkStage }) {
  const artefact = useEsocialPortalWalkArtefact();
  const { notify } = useNotifications();
  const label = WALK_STAGE_LABELS[stage.name];

  const download = (kind: 'html' | 'file') =>
    artefact.mutate(
      { runId, index: stage.index, artefact: kind },
      {
        onSuccess: ({ blob, filename }) =>
          saveAsAttachment(
            blob,
            filename ??
              (kind === 'html'
                ? `portal-walk-${runId}-${String(stage.index).padStart(2, '0')}-html.html`
                : (stage.file_name ??
                  `portal-walk-${runId}-${String(stage.index).padStart(2, '0')}-file`)),
          ),
        onError: () => notify('error', PORTAL_WALK_COPY.downloadFailed),
      },
    );

  return (
    <section className="screenshot" aria-label={PORTAL_WALK_COPY.evidenceTitle(label)}>
      {stage.artefacts.includes(Artefact.SCREENSHOT) && (
        <StageScreenshot runId={runId} index={stage.index} label={label} />
      )}
      {stage.certificate_hops !== null && stage.certificate_hops !== undefined && (
        <CertificateHops hops={stage.certificate_hops} label={label} />
      )}
      {((stage.action_screenshots ?? []).length > 0 ||
        (stage.action_screenshots_dropped ?? 0) > 0) && (
        <ActionScreenshots runId={runId} stage={stage} />
      )}
      {stage.artefacts.includes(Artefact.TRACE) && (
        <StageTrace runId={runId} index={stage.index} label={label} />
      )}
      <div className="form__actions">
        {stage.artefacts.includes(Artefact.HTML) && (
          <button
            type="button"
            className="button button--ghost"
            disabled={artefact.isPending}
            onClick={() => download('html')}
          >
            {PORTAL_WALK_COPY.downloadHtml}
          </button>
        )}
        {stage.artefacts.includes(Artefact.FILE) && (
          <button
            type="button"
            className="button button--ghost"
            disabled={artefact.isPending}
            onClick={() => download('file')}
          >
            {PORTAL_WALK_COPY.downloadFile}
            {stage.file_name ? ` (${stage.file_name})` : ''}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * One screenshot, shown through an object URL that lives exactly as long as
 * the image element showing it.
 *
 * The URL is made and revoked in the image's ref callback, so unmounting the
 * stage -- closing the run, switching tab, leaving the page -- revokes it, and
 * no `blob:` address outlives the screen. The bytes leave the query cache at
 * the same moment (`gcTime: 0`).
 */
function StageScreenshot({ runId, index, label }: { runId: string; index: number; label: string }) {
  const shot = useEsocialPortalWalkScreenshot(runId, index);
  const blob = shot.data;
  const attach = useBlobSource(blob);

  if (shot.isError) {
    return (
      <p className="panel__footnote panel__footnote--warning">
        {PORTAL_WALK_COPY.screenshotFailed}
      </p>
    );
  }
  if (blob === undefined) {
    return <p className="table__muted">{PORTAL_WALK_COPY.screenshotLoading}</p>;
  }
  return (
    <img ref={attach} className="screenshot__image" alt={PORTAL_WALK_COPY.screenshotAlt(label)} />
  );
}

/** An object URL for `blob` on `image`, revoked when the image goes -- `StageScreenshot`'s rule. */
function useBlobSource(blob: Blob | undefined) {
  return useCallback(
    (image: HTMLImageElement | null) => {
      if (image === null || blob === undefined) {
        return undefined;
      }
      const url = URL.createObjectURL(blob);
      image.src = url;
      return () => {
        image.removeAttribute('src');
        URL.revokeObjectURL(url);
      };
    },
    [blob],
  );
}

/**
 * The viewport after each action of the stage, as a strip of thumbnails in
 * action order. Pressing one opens it full size under the strip; pressing it
 * again, or the close button, puts it away. Each image is one audited read.
 */
function ActionScreenshots({ runId, stage }: { runId: string; stage: EsocialPortalWalkStage }) {
  const [open, setOpen] = useState<number | null>(null);
  const shots = stage.action_screenshots ?? [];
  const dropped = stage.action_screenshots_dropped ?? 0;
  const opened = shots.find((shot) => shot.number === open);

  return (
    <section className="action-shots" aria-label={PORTAL_WALK_COPY.actionsTitle}>
      <h4 className="action-shots__title">{PORTAL_WALK_COPY.actionsTitle}</h4>
      <p className="table__muted">{PORTAL_WALK_COPY.actionsHint}</p>
      <ol className="action-shots__strip">
        {shots.map((shot) => (
          <li key={shot.number}>
            <button
              type="button"
              className="action-shots__thumb"
              aria-pressed={open === shot.number}
              aria-label={PORTAL_WALK_COPY.thumbnailLabel(
                shot.number,
                WALK_ACTION_LABELS[shot.action],
                shot.target ?? '—',
              )}
              onClick={() => setOpen(open === shot.number ? null : shot.number)}
            >
              <ActionThumbnail runId={runId} index={stage.index} shot={shot} />
              <span className="action-shots__caption">
                {String(shot.number).padStart(2, '0')} · {WALK_ACTION_LABELS[shot.action]}
              </span>
            </button>
          </li>
        ))}
      </ol>
      {dropped > 0 && <p className="table__muted">{PORTAL_WALK_COPY.actionsDropped(dropped)}</p>}
      {opened !== undefined && (
        <figure className="action-shots__full">
          <ActionThumbnail runId={runId} index={stage.index} shot={opened} full />
          <figcaption>
            {PORTAL_WALK_COPY.thumbnailLabel(
              opened.number,
              WALK_ACTION_LABELS[opened.action],
              opened.target ?? '—',
            )}{' '}
            <button type="button" className="button button--ghost" onClick={() => setOpen(null)}>
              {PORTAL_WALK_COPY.closeFull}
            </button>
          </figcaption>
        </figure>
      )}
    </section>
  );
}

function ActionThumbnail({
  runId,
  index,
  shot,
  full = false,
}: {
  runId: string;
  index: number;
  shot: EsocialPortalWalkActionScreenshot;
  full?: boolean;
}) {
  const image = useEsocialPortalWalkActionScreenshot(runId, index, shot.number);
  const attach = useBlobSource(image.data);
  const alt = PORTAL_WALK_COPY.thumbnailAlt(shot.number, WALK_ACTION_LABELS[shot.action]);
  if (image.isError) {
    return <span className="table__muted">{PORTAL_WALK_COPY.thumbnailFailed}</span>;
  }
  if (image.data === undefined) {
    return <span className="table__muted">{PORTAL_WALK_COPY.screenshotLoading}</span>;
  }
  return (
    <img ref={attach} className={full ? 'screenshot__image' : 'action-shots__image'} alt={alt} />
  );
}

/** ``host/path`` of an address the API already stripped of its query. */
function hostAndPath(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.host}${parsed.pathname}`;
  } catch {
    return url;
  }
}

/**
 * The certificate leg of #457: the requests the backend made with the A1
 * after gov.br's certificate option -- method, host and path, status, and
 * where each went -- up to the hand-over to the browser or the failure.
 */
function CertificateHops({
  hops,
  label,
}: {
  hops: readonly EsocialPortalWalkCertificateHop[];
  label: string;
}) {
  const columns: readonly Column<EsocialPortalWalkCertificateHop>[] = [
    { key: 'method', header: PORTAL_WALK_COPY.method, render: (hop) => hop.method },
    {
      key: 'address',
      header: PORTAL_WALK_COPY.address,
      render: (hop) => <code className="trace__url">{hostAndPath(hop.url)}</code>,
    },
    {
      key: 'status',
      header: PORTAL_WALK_COPY.hopStatus,
      render: (hop) =>
        hop.status === null || hop.status === undefined
          ? PORTAL_WALK_COPY.hopFailed
          : String(hop.status),
    },
    {
      key: 'went',
      header: PORTAL_WALK_COPY.hopWentTo,
      render: (hop) =>
        hop.location ? (
          <code className="trace__url">{hostAndPath(hop.location)}</code>
        ) : hop.status === null || hop.status === undefined ? (
          PORTAL_WALK_COPY.noAnswer
        ) : (
          PORTAL_WALK_COPY.hopPage
        ),
    },
    {
      key: 'cookies',
      header: PORTAL_WALK_COPY.hopCookies,
      render: (hop) => ((hop.cookies_set ?? []).length > 0 ? hop.cookies_set?.join(', ') : '—'),
    },
  ];

  return (
    <section className="trace" aria-label={PORTAL_WALK_COPY.hopsCaption(label)}>
      <h4 className="trace__title">{PORTAL_WALK_COPY.hopsTitle}</h4>
      <p className="table__muted">{PORTAL_WALK_COPY.hopsHint}</p>
      <DataTable
        caption={PORTAL_WALK_COPY.hopsCaption(label)}
        columnVisibility={false}
        columns={columns}
        rows={hops}
        rowKey={(hop, position) => `${position}-${hop.method}-${hop.url}`}
        emptyTitle={PORTAL_WALK_COPY.hopsEmpty}
      />
    </section>
  );
}

function formatMilliseconds(value: number | null): string {
  return value === null ? '—' : `${formatInteger(value)} ms`;
}

function requestResult(request: PortalWalkTraceRequest): string {
  if (request.status !== null) {
    return String(request.status);
  }
  if (request.failure !== null) {
    return request.failure;
  }
  return request.ended_ms === null ? PORTAL_WALK_COPY.noAnswer : '—';
}

/**
 * The stage's network table -- method, address, status or failure, duration
 * -- with the requests that had no answer when the stage ended marked, and
 * the console's lines when there are any. Read from the sealed trace.
 */
function StageTrace({ runId, index, label }: { runId: string; index: number; label: string }) {
  const trace = useEsocialPortalWalkTrace(runId, index);

  if (trace.isError) {
    return (
      <p className="panel__footnote panel__footnote--warning">{PORTAL_WALK_COPY.traceFailed}</p>
    );
  }
  if (trace.data === undefined) {
    return <p className="table__muted">{PORTAL_WALK_COPY.traceLoading}</p>;
  }
  const data = trace.data;
  const pendingCount = data.requests.filter((request) => request.ended_ms === null).length;

  const columns: readonly Column<PortalWalkTraceRequest>[] = [
    { key: 'method', header: PORTAL_WALK_COPY.method, render: (request) => request.method },
    {
      key: 'address',
      header: PORTAL_WALK_COPY.address,
      render: (request) => (
        <>
          <code className="trace__url">{request.url}</code>
          {request.ended_ms === null && (
            <>
              {' '}
              <span className="badge badge--blocking">{PORTAL_WALK_COPY.pending}</span>
            </>
          )}
        </>
      ),
    },
    { key: 'result', header: PORTAL_WALK_COPY.result, render: requestResult },
    {
      key: 'duration',
      header: PORTAL_WALK_COPY.duration,
      render: (request) => formatMilliseconds(request.duration_ms),
    },
  ];

  return (
    <section className="trace" aria-label={PORTAL_WALK_COPY.networkCaption(label)}>
      <h4 className="trace__title">{PORTAL_WALK_COPY.networkTitle}</h4>
      {data.certificate_failure !== null && (
        <p className="panel__footnote panel__footnote--warning">
          {PORTAL_WALK_COPY.certificateFailure}: {data.certificate_failure}
        </p>
      )}
      {pendingCount > 0 && (
        <p className="panel__footnote panel__footnote--warning">
          {PORTAL_WALK_COPY.pendingSummary(pendingCount)}
        </p>
      )}
      {data.cap.hit && <p className="table__muted">{PORTAL_WALK_COPY.capHit}</p>}
      <DataTable
        caption={PORTAL_WALK_COPY.networkCaption(label)}
        columnVisibility={false}
        columns={columns}
        rows={data.requests}
        rowKey={(request, position) => `${position}-${request.started_ms}`}
        emptyTitle={PORTAL_WALK_COPY.networkEmpty}
      />
      {data.console.length > 0 && (
        <>
          <h4 className="trace__title">{PORTAL_WALK_COPY.consoleTitle}</h4>
          <ul className="trace__console">
            {data.console.map((line, position) => (
              <li key={`${position}-${line.at_ms}`}>
                <span className="badge badge--muted">{line.level}</span> <code>{line.text}</code>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
